import { AccessoryDialog } from '@/components/AccessoryDialog';
import { BillingCenterSelect } from '@/components/BillingCenterSelect';
import { AccessoryFilters } from '@/components/catalog/AccessoryFilters';
import { CatalogFilters } from '@/components/catalog/CatalogFilters';
import { ForfaitsTab } from '@/components/catalog/ForfaitsTab';
import { TravelEsimTab } from '@/components/catalog/TravelEsimTab';
import { PermissionGuard } from '@/components/PermissionGuard';
import { QuantitySelector } from '@/components/QuantitySelector';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAccessoryFilters } from '@/hooks/useAccessoryFilters';
import { useAccessories, useDeviceModels } from '@/hooks/useCatalog';
import { useAddAccessoryToCheckout, useAddDeviceVariantToCheckout } from '@/hooks/useCatalogActions';
import { useCatalogFilters } from '@/hooks/useCatalogFilters';
import { useDefaultSite } from '@/hooks/useDefaultSite';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import type { Accessory, DeviceModel, DeviceVariant } from '@octave/contracts';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  Globe,
  Headphones,
  Loader2,
  Package,
  Plus,
  ShoppingCart,
  Signal,
  Smartphone,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatPrice } from '@/lib/format';

const log = createLogger('CatalogView');

const COMMON_COLORS = [
  'Noir',
  'Rose',
  'Bleu',
  'Rouge',
  'Violet',
  'Vert',
  'Transparent',
];

export function CatalogView() {
  const [selectedModel, setSelectedModel] = useState<DeviceModel | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<DeviceVariant | null>(
    null
  );
  const [isDeviceDialogOpen, setIsDeviceDialogOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const [selectedAccessory, setSelectedAccessory] = useState<Accessory | null>(
    null
  );
  const [
    isAccessoryCompatibilityDialogOpen,
    setIsAccessoryCompatibilityDialogOpen,
  ] = useState(false);
  const [isAccessoryDialogOpen, setIsAccessoryDialogOpen] = useState(false);
  const [selectedCompatibleDevice, setSelectedCompatibleDevice] =
    useState<DeviceVariant | null>(null);
  const [accessoryQuantity, setAccessoryQuantity] = useState(1);

  const [selectedModelCapacity, setSelectedModelCapacity] = useState<
    number | null
  >(null);
  const [selectedModelColor, setSelectedModelColor] = useState<string | null>(
    null
  );

  const [selectedCompatibleModel, setSelectedCompatibleModel] =
    useState<DeviceModel | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectionStep, setSelectionStep] = useState<
    'brand' | 'model' | 'color'
  >('brand');
  const [selectedAccessoryColor, setSelectedAccessoryColor] = useState<
    string | null
  >(null);

  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const { data: organization } = useCurrentOrganization();

  // The open tab lives in the URL so the line detail can land straight on
  // the international eSIM tab and a reload keeps the tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab =
    tabParam === 'accessories' ||
    tabParam === 'forfaits' ||
    tabParam === 'travel-esim'
      ? tabParam
      : 'devices';
  const selectTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const { currentSite } = useDefaultSite();
  const { data: deviceModels, isLoading: modelsLoading } =
    useDeviceModels(true);
  const { data: accessories, isLoading: accessoriesLoading } =
    useAccessories(true);

  const {
    filters: deviceFilters,
    filteredModels: filteredDeviceModels,
    updateFilters: updateDeviceFilters,
  } = useCatalogFilters(deviceModels);

  const {
    filters: accessoryFilters,
    filteredAccessories: filteredAccessoryList,
    updateFilters: updateAccessoryFilters,
  } = useAccessoryFilters(accessories);

  const addDeviceVariantToCheckout = useAddDeviceVariantToCheckout();
  const addAccessoryToCheckout = useAddAccessoryToCheckout();

  // --- Handlers ---

  const handleModelSelect = (model: DeviceModel) => {
    setSelectedModel(model);
    setSelectedVariant(null);
    setSelectedModelCapacity(null);
    setSelectedModelColor(null);
    setSelectedSiteId(currentSite?.id ?? null);
    setIsDeviceDialogOpen(true);
  };

  const handleVariantSelect = async (variant: DeviceVariant) => {
    setSelectedVariant(variant);
    try {
      if (!organization || !selectedSiteId) return;
      await addDeviceVariantToCheckout.mutateAsync({
        organizationId: organization.id,
        deviceVariantId: variant.id,
        quantity,
        siteId: selectedSiteId,
      });
      handleCloseDeviceDialog();
    } catch (error) {
      log.error('Error adding to checkout', error);
    }
  };

  const handleAddAccessory = async (accessory: Accessory) => {
    setSelectedAccessory(accessory);
    setSelectedSiteId(currentSite?.id ?? null);
    if (accessory.requiresCompatibility) {
      setSelectedCompatibleModel(null);
      setSelectedCompatibleDevice(null);
      setIsAccessoryCompatibilityDialogOpen(true);
    } else {
      setIsAccessoryDialogOpen(true);
    }
  };

  const handleAccessoryWithCompatibility = async () => {
    if (
      !selectedAccessory ||
      !selectedCompatibleDevice ||
      !organization ||
      !selectedSiteId
    )
      return;
    try {
      await addAccessoryToCheckout.mutateAsync({
        organizationId: organization.id,
        accessoryId: selectedAccessory.id,
        quantity: accessoryQuantity,
        siteId: selectedSiteId,
        deviceVariantId: selectedCompatibleDevice.id,
        color: selectedAccessoryColor || undefined,
      });
      handleCloseCompatibilityDialog();
    } catch (error) {
      log.error('Error adding accessory', error);
    }
  };

  const handleSimpleAccessoryAdd = async (
    accessory: Accessory,
    qty: number
  ) => {
    try {
      if (!organization || !selectedSiteId) return;
      await addAccessoryToCheckout.mutateAsync({
        organizationId: organization.id,
        accessoryId: accessory.id,
        quantity: qty,
        siteId: selectedSiteId,
      });
      setIsAccessoryDialogOpen(false);
      setSelectedAccessory(null);
    } catch (error) {
      log.error('Error adding accessory', error);
    }
  };

  // Utility
  const getAvailableCapacities = (model: DeviceModel) => {
    if (!model.variants) return [];
    const capacities = new Set(
      model.variants
        .map((v) => v.capacity)
        .filter((c): c is number => c !== undefined && c !== null)
    );
    return Array.from(capacities).sort((a, b) => a - b);
  };

  const getAvailableColors = (model: DeviceModel, capacity: number) => {
    if (!model.variants) return [];
    const colors = new Set(
      model.variants
        .filter((v) => v.capacity === capacity)
        .map((v) => v.color)
        .filter((c): c is string => Boolean(c))
    );
    return Array.from(colors);
  };

  const getFilteredVariants = (
    model: DeviceModel,
    capacity?: number,
    color?: string
  ) => {
    if (!model.variants) return [];
    return model.variants.filter((v) => {
      if (capacity && v.capacity !== capacity) return false;
      if (color && v.color !== color) return false;
      return true;
    });
  };

  const handleModelCapacitySelect = (capacity: number) => {
    setSelectedModelCapacity(capacity);
    setSelectedModelColor(null);
    setSelectedVariant(null);
  };

  const handleModelColorSelect = (color: string) => {
    setSelectedModelColor(color);
    if (selectedModel && selectedModelCapacity) {
      const variant = getFilteredVariants(
        selectedModel,
        selectedModelCapacity,
        color
      )[0];
      if (variant) setSelectedVariant(variant);
    }
  };

  const handleCompatibleModelSelect = (model: DeviceModel) => {
    setSelectedCompatibleModel(model);
    if (model.variants && model.variants.length > 0) {
      setSelectedCompatibleDevice(model.variants[0]);
    }
    if (selectedAccessory?.hasColors) {
      setSelectionStep('color');
    }
  };

  const handleBrandSelect = (brand: string) => {
    setSelectedBrand(brand);
    setSelectionStep('model');
  };

  const handleCloseDeviceDialog = () => {
    setIsDeviceDialogOpen(false);
    setSelectedModel(null);
    setSelectedVariant(null);
    setSelectedModelCapacity(null);
    setSelectedModelColor(null);
    setQuantity(1);
    setSelectedSiteId(null);
  };

  const handleCloseCompatibilityDialog = () => {
    setIsAccessoryCompatibilityDialogOpen(false);
    setSelectedAccessory(null);
    setSelectedCompatibleModel(null);
    setSelectedCompatibleDevice(null);
    setAccessoryQuantity(1);
    setSelectedBrand('');
    setSelectionStep('brand');
    setSelectedAccessoryColor(null);
    setSelectedSiteId(null);
  };

  const uniqueBrands = deviceModels
    ? [...new Set(deviceModels.map((m) => m.brand))].sort()
    : [];

  const filteredModels = deviceModels
    ? deviceModels.filter((m) => m.brand === selectedBrand)
    : [];

  
  // --- Loading ---
  if (modelsLoading || accessoriesLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Skeleton className="h-12 w-80 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catalogue</h1>
          <p className="text-sm text-slate-500 mt-1">
            Découvrez nos appareils, accessoires, forfaits et eSIM
            internationales
          </p>
        </div>
        {activeTab !== 'travel-esim' && (
        <PermissionGuard permission="write">
          <Button
            size="sm"
            onClick={() => navigate(`/o/${tenantSlug}/lines/new`)}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
          >
            <Plus className="h-3.5 w-3.5" />
            Commander une ligne
          </Button>
        </PermissionGuard>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={selectTab} className="space-y-6">
        <TabsList className="bg-white shadow-sm rounded-xl h-12 p-1 w-full sm:w-auto">
          <TabsTrigger
            value="devices"
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
          >
            <Smartphone className="h-4 w-4" />
            Appareils
          </TabsTrigger>
          <TabsTrigger
            value="accessories"
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
          >
            <Headphones className="h-4 w-4" />
            Accessoires
          </TabsTrigger>
          <TabsTrigger
            value="forfaits"
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
          >
            <Signal className="h-4 w-4" />
            Forfaits
          </TabsTrigger>
          <TabsTrigger
            value="travel-esim"
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
          >
            <Globe className="h-4 w-4" />
            eSIM internationale
          </TabsTrigger>
        </TabsList>

        {/* ── Devices tab ── */}
        <TabsContent value="devices" className="space-y-6">
          <CatalogFilters
            deviceModels={deviceModels || []}
            filters={deviceFilters}
            onFiltersChange={updateDeviceFilters}
          />

          <p className="text-xs text-slate-400">
            {filteredDeviceModels?.filter(
              (m) => m.variants && m.variants.length > 0
            ).length || 0}{' '}
            appareil(s)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredDeviceModels
              ?.filter((m) => m.variants && m.variants.length > 0)
              .map((model) => (
                <div
                  key={model.id}
                  onClick={() => handleModelSelect(model)}
                  className="group cursor-pointer rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-transparent hover:border-emerald-100"
                >
                  <div className="aspect-square flex items-center justify-center bg-slate-50 p-6">
                    {model.imageUrl ? (
                      <img
                        src={model.imageUrl}
                        alt={model.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <Smartphone className="h-16 w-16 text-slate-200" />
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {model.name}
                      </p>
                      <p className="text-xs text-slate-400">{model.brand}</p>
                    </div>
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                      {model.category}
                    </span>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-400">À partir de</p>
                        <p className="text-lg font-bold text-slate-900">
                          {formatPrice(
                            Math.min(...model.variants!.map((v) => v.price))
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleModelSelect(model);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm hover:shadow-md transition-all"
                      >
                        Choisir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </TabsContent>

        {/* ── Accessories tab ── */}
        <TabsContent value="accessories" className="space-y-6">
          <AccessoryFilters
            accessories={accessories || []}
            filters={accessoryFilters}
            onFiltersChange={updateAccessoryFilters}
          />

          <p className="text-xs text-slate-400">
            {filteredAccessoryList?.length || 0} accessoire(s)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAccessoryList?.map((accessory) => (
              <div
                key={accessory.id}
                onClick={() => handleAddAccessory(accessory)}
                className="group cursor-pointer rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-transparent hover:border-emerald-100"
              >
                <div className="aspect-square flex items-center justify-center bg-slate-50 p-6">
                  {accessory.imageUrl ? (
                    <img
                      src={accessory.imageUrl}
                      alt={accessory.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <Package className="h-16 w-16 text-slate-200" />
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {accessory.name}
                    </p>
                  </div>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                    {accessory.category}
                  </span>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-400">À l'acte</p>
                      <p className="text-lg font-bold text-slate-900">
                        {formatPrice(accessory.price)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddAccessory(accessory);
                      }}
                      disabled={addAccessoryToCheckout.isPending}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm hover:shadow-md transition-all"
                    >
                      {accessory.requiresCompatibility
                        ? 'Configurer'
                        : 'Choisir'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ── Forfaits tab ── */}
        <TabsContent value="forfaits" className="space-y-6">
          <ForfaitsTab />
        </TabsContent>

        {/* ── International eSIM tab ── */}
        <TabsContent value="travel-esim" className="space-y-6">
          <TravelEsimTab />
        </TabsContent>
      </Tabs>

      {/* ── Device variant selection dialog ── */}
      <Dialog open={isDeviceDialogOpen} onOpenChange={handleCloseDeviceDialog}>
        <DialogContent className="!max-w-5xl max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
            <DialogTitle className="text-lg font-bold text-slate-900">
              {selectedModel?.brand} {selectedModel?.name}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Choisissez la déclinaison qui vous convient
            </p>
          </DialogHeader>

          {selectedModel && (
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Image */}
                <div className="aspect-square flex items-center justify-center rounded-2xl bg-slate-50 p-8">
                  {selectedModel.imageUrl ? (
                    <img
                      src={selectedModel.imageUrl}
                      alt={selectedModel.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Smartphone className="h-24 w-24 text-slate-200" />
                  )}
                </div>

                {/* Configuration */}
                <div className="space-y-6">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Choisissez votre configuration
                  </h3>

                  {selectedModel.variants &&
                  selectedModel.variants.length > 0 ? (
                    <div className="space-y-6">
                      {/* Step 1: Capacity */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-400">
                          1. Capacité
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {getAvailableCapacities(selectedModel).map(
                            (capacity) => (
                              <button
                                key={capacity}
                                onClick={() =>
                                  handleModelCapacitySelect(capacity)
                                }
                                className={cn(
                                  'rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
                                  selectedModelCapacity === capacity
                                    ? 'bg-slate-900 text-white shadow-sm'
                                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                                )}
                              >
                                {capacity >= 1024
                                  ? `${capacity / 1024} To`
                                  : `${capacity} Go`}
                              </button>
                            )
                          )}
                        </div>
                      </div>

                      {/* Step 2: Color */}
                      {selectedModelCapacity &&
                        getAvailableColors(
                          selectedModel,
                          selectedModelCapacity
                        ).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-400">
                              2. Couleur
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {getAvailableColors(
                                selectedModel,
                                selectedModelCapacity
                              ).map((color) => (
                                <button
                                  key={color}
                                  onClick={() =>
                                    handleModelColorSelect(color)
                                  }
                                  className={cn(
                                    'rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
                                    selectedModelColor === color
                                      ? 'bg-slate-900 text-white shadow-sm'
                                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                                  )}
                                >
                                  {color}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Selected variant preview */}
                      {selectedVariant && (
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {selectedVariant.name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {selectedVariant.condition === 'NEW'
                                  ? 'Neuf'
                                  : 'Reconditionné'}
                              </p>
                            </div>
                            <p className="text-lg font-bold text-slate-900">
                              {formatPrice(selectedVariant.price)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center py-12 text-slate-400">
                      <Package className="h-10 w-10 mb-3 text-slate-300" />
                      <p className="text-sm">Aucune déclinaison disponible</p>
                    </div>
                  )}

                  {/* Cart section */}
                  {selectedVariant && (
                    <div className="rounded-xl bg-slate-50 p-5 space-y-4">
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-slate-700">
                          Centre de facturation
                        </span>
                        <BillingCenterSelect
                          value={selectedSiteId}
                          onChange={setSelectedSiteId}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">
                          Quantité
                        </span>
                        <QuantitySelector
                          value={quantity}
                          onChange={setQuantity}
                          min={1}
                        />
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <span className="text-sm font-semibold text-slate-900">
                          Total
                        </span>
                        <span className="text-xl font-bold text-slate-900">
                          {formatPrice(selectedVariant.price * quantity)}
                        </span>
                      </div>
                      <Button
                        className="w-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm hover:shadow-md transition-all"
                        onClick={() => handleVariantSelect(selectedVariant)}
                        disabled={
                          addDeviceVariantToCheckout.isPending ||
                          !selectedSiteId
                        }
                      >
                        {addDeviceVariantToCheckout.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Ajout en cours...
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="h-4 w-4" />
                            Ajouter au panier
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Accessory compatibility dialog ── */}
      <Dialog
        open={isAccessoryCompatibilityDialogOpen}
        onOpenChange={handleCloseCompatibilityDialog}
      >
        <DialogContent className="!max-w-5xl max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
            <DialogTitle className="text-lg font-bold text-slate-900">
              Sélectionner un appareil compatible
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Choisissez l'appareil pour {selectedAccessory?.name}
            </p>
          </DialogHeader>

          {selectedAccessory && (
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Accessory preview */}
                <div className="space-y-4">
                  <div className="aspect-square flex items-center justify-center rounded-2xl bg-slate-50 p-8">
                    {selectedAccessory.imageUrl ? (
                      <img
                        src={selectedAccessory.imageUrl}
                        alt={selectedAccessory.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Package className="h-24 w-24 text-slate-200" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {selectedAccessory.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {selectedAccessory.category}
                    </p>
                    <p className="text-lg font-bold text-slate-900 mt-2">
                      {formatPrice(selectedAccessory.price)}
                    </p>
                  </div>
                </div>

                {/* Selection steps */}
                <div className="space-y-6">
                  {/* Navigation */}
                  <div className="space-y-2">
                    {(selectionStep === 'model' ||
                      selectionStep === 'color') && (
                      <button
                        onClick={
                          selectionStep === 'model'
                            ? () => {
                                setSelectedBrand('');
                                setSelectionStep('brand');
                                setSelectedCompatibleModel(null);
                              }
                            : () => {
                                setSelectionStep('model');
                                setSelectedAccessoryColor(null);
                              }
                        }
                        className="group flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <ChevronLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
                        {selectionStep === 'model'
                          ? 'Retour aux marques'
                          : 'Retour aux modèles'}
                      </button>
                    )}
                    <h3 className="text-sm font-semibold text-slate-900">
                      {selectionStep === 'brand'
                        ? 'Choisissez la marque'
                        : selectionStep === 'model'
                          ? `Modèles ${selectedBrand}`
                          : 'Choisissez la couleur'}
                    </h3>
                  </div>

                  {selectionStep === 'brand' ? (
                    <div className="grid grid-cols-2 gap-3">
                      {uniqueBrands.map((brand) => {
                        const count =
                          deviceModels?.filter((m) => m.brand === brand)
                            .length || 0;
                        return (
                          <button
                            key={brand}
                            onClick={() => handleBrandSelect(brand)}
                            className="rounded-xl bg-white border border-slate-200 p-4 text-left transition-all hover:shadow-md hover:border-emerald-200"
                          >
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 mb-3">
                              <span className="text-lg font-bold text-slate-600">
                                {brand.charAt(0)}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-slate-900">
                              {brand}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {count} modèle{count > 1 ? 's' : ''}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  ) : selectionStep === 'model' ? (
                    <div className="space-y-2">
                      {filteredModels
                        .filter(
                          (m) => m.variants && m.variants.length > 0
                        )
                        .map((model) => (
                          <button
                            key={model.id}
                            onClick={() =>
                              handleCompatibleModelSelect(model)
                            }
                            className={cn(
                              'flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left transition-all',
                              selectedCompatibleModel?.id === model.id
                                ? 'bg-emerald-50 border border-emerald-200'
                                : 'bg-white border border-slate-200 hover:border-emerald-200 hover:shadow-sm'
                            )}
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-50 overflow-hidden">
                              {model.imageUrl ? (
                                <img
                                  src={model.imageUrl}
                                  alt={model.name}
                                  className="h-8 w-8 object-contain"
                                />
                              ) : (
                                <Smartphone className="h-4 w-4 text-slate-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900">
                                {model.name}
                              </p>
                              {model.description && (
                                <p className="text-xs text-slate-400 truncate">
                                  {model.description}
                                </p>
                              )}
                            </div>
                            {selectedCompatibleModel?.id === model.id && (
                              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shrink-0" />
                            )}
                          </button>
                        ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {COMMON_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedAccessoryColor(color)}
                          className={cn(
                            'rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
                            selectedAccessoryColor === color
                              ? 'bg-slate-900 text-white shadow-sm'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                          )}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Cart section */}
                  {selectedCompatibleModel &&
                    (!selectedAccessory?.hasColors ||
                      selectedAccessoryColor) && (
                      <div className="rounded-xl bg-slate-50 p-5 space-y-4">
                        <div className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 p-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 overflow-hidden">
                            {selectedCompatibleModel.imageUrl ? (
                              <img
                                src={selectedCompatibleModel.imageUrl}
                                alt={selectedCompatibleModel.name}
                                className="h-6 w-6 object-contain"
                              />
                            ) : (
                              <Smartphone className="h-3.5 w-3.5 text-slate-300" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-700">
                              Compatible avec {selectedCompatibleModel.brand}{' '}
                              {selectedCompatibleModel.name}
                            </p>
                            {selectedAccessoryColor && (
                              <p className="text-[10px] text-slate-400">
                                Couleur : {selectedAccessoryColor}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <span className="text-sm font-medium text-slate-700">
                            Centre de facturation
                          </span>
                          <BillingCenterSelect
                            value={selectedSiteId}
                            onChange={setSelectedSiteId}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">
                            Quantité
                          </span>
                          <QuantitySelector
                            value={accessoryQuantity}
                            onChange={setAccessoryQuantity}
                            min={1}
                          />
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                          <span className="text-sm font-semibold text-slate-900">
                            Total
                          </span>
                          <span className="text-xl font-bold text-slate-900">
                            {formatPrice(
                              selectedAccessory.price * accessoryQuantity
                            )}
                          </span>
                        </div>

                        <Button
                          className="w-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm hover:shadow-md transition-all"
                          onClick={handleAccessoryWithCompatibility}
                          disabled={
                            addAccessoryToCheckout.isPending || !selectedSiteId
                          }
                        >
                          {addAccessoryToCheckout.isPending ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Ajout en cours...
                            </>
                          ) : (
                            <>
                              <ShoppingCart className="h-4 w-4" />
                              Ajouter au panier
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Simple accessory dialog */}
      <AccessoryDialog
        accessory={selectedAccessory}
        isOpen={isAccessoryDialogOpen}
        onClose={() => {
          setIsAccessoryDialogOpen(false);
          setSelectedAccessory(null);
          setSelectedSiteId(null);
        }}
        onAddToCart={handleSimpleAccessoryAdd}
        isLoading={addAccessoryToCheckout.isPending}
        siteId={selectedSiteId}
        onSiteIdChange={setSelectedSiteId}
      />
    </div>
  );
}
